/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useAuth } from '../core/Auth'
import { login, loginVerify } from '../core/_requests'
import Swal from 'sweetalert2'
import { featureFlags } from '../../../../config/countryConfig'
import { useIntl } from 'react-intl'

const initialValues = {
  tm_id: '',
}

/*
  formikGetOtp+YUP+Typescript:
  https://jaredpalmer.com/formikGetOtp/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formikGetOtp-6c342578a20e
*/

const initialValuesOTP: any = {
  otp: {
    a: '',
    b: '',
    c: '',
    d: '',
    e: '',
    f: '',
  },
}
const theme = localStorage.getItem('kt_theme_mode_value')
const RESEND_COOLDOWN_SECONDS = 120
const MAX_FAILED_ATTEMPTS = 3

export function Login() {
  const intl = useIntl()

  const loginSchema = Yup.object().shape({
    tm_id: Yup.string()
      .trim()
      .min(3, intl.formatMessage({ id: 'AUTH.LOGIN.VALIDATION_MIN_CHARS', defaultMessage: 'Minimum 3 characters' }))
      .max(100, intl.formatMessage({ id: 'AUTH.LOGIN.VALIDATION_MAX_CHARS', defaultMessage: 'Maximum 100 characters' }))
      .required(
        intl.formatMessage({
          id: featureFlags.onboarding ? 'AUTH.LOGIN.VALIDATION_ORG_ID_REQUIRED' : 'AUTH.LOGIN.VALIDATION_TM_ID_REQUIRED',
          defaultMessage: featureFlags.onboarding ? 'Telemarketer ID or email is required' : 'TM ID is required',
        })
      ),
  })

  const [loading, setLoading] = useState(false)
  const [isOtpSent, setIsOtpSent] = useState(false)
  const { saveAuth } = useAuth()
  const navigate = useNavigate()
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [isAccountLocked, setIsAccountLocked] = useState(false)
  const [otpResendMessage, setOtpResendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showInfo, setShowInfo] = useState(false)

  const handleResendOtp = async () => {
    if (isAccountLocked) {
      setIsAccountLocked(true)
      setResendDisabled(true)
      setOtpResendMessage({ type: 'error', text: intl.formatMessage({ id: 'AUTH.LOGIN.ACCOUNT_LOCKED_ERROR' }) })
      return
    }

    setResendDisabled(true)
    setResendTimer(RESEND_COOLDOWN_SECONDS)
    setOtpResendMessage(null)

    try {
      await login(formikGetOtp.values.tm_id)
      setOtpResendMessage({ type: 'success', text: intl.formatMessage({ id: 'AUTH.LOGIN.RESEND_SUCCESS_TEXT' }) })
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || intl.formatMessage({ id: 'AUTH.LOGIN.RESEND_FAILED_TEXT' })

      if ((errorMessage as string).toLowerCase().includes('lock')) {
        setIsAccountLocked(true)
        setResendDisabled(true)
      }

      setOtpResendMessage({ type: 'error', text: errorMessage })
    }
  }

  useEffect(() => {
    if (otpResendMessage) {
      const timeout = setTimeout(() => setOtpResendMessage(null), 4000)
      return () => clearTimeout(timeout)
    }
  }, [otpResendMessage])
  useEffect(() => {
    let timerInterval: any

    if (resendTimer > 0 && !isAccountLocked) {
      timerInterval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev === 1) {
            setResendDisabled(false)
            clearInterval(timerInterval)
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => clearInterval(timerInterval)
  }, [resendTimer, isAccountLocked])

  const formikGetOtp = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        await login(values.tm_id)
        Swal.fire({
          title: intl.formatMessage({ id: 'AUTH.LOGIN.OTP_SENT_TITLE' }),
          text: intl.formatMessage({
            id: featureFlags.onboarding
              ? 'AUTH.LOGIN.OTP_SENT_SUBTITLE_LA'
              : 'AUTH.LOGIN.OTP_SENT_SUBTITLE',
            defaultMessage: featureFlags.onboarding
              ? 'OTP is sent to your verified email.'
              : 'OTP is sent to the DLT-registered contact number and email ID.',
          }),
          icon: 'success',
        })
        setIsOtpSent(true)
        setFailedAttempts(0)
        setIsAccountLocked(false)
        setResendDisabled(true)
        setResendTimer(RESEND_COOLDOWN_SECONDS)
        setLoading(false)
      } catch (error: any) {
        console.error(error)
        setStatus(error?.response?.data?.data?.detail || error?.response?.data?.message || intl.formatMessage({ id: 'AUTH.LOGIN.ERROR_INCORRECT_DETAILS' }))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const formikSubmitOtp = useFormik({
    initialValues: initialValuesOTP,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const otp =
          values.otp.a + values.otp.b + values.otp.c + values.otp.d + values.otp.e + values.otp.f
        if (!otp || otp.length !== 6) {
          setStatus(intl.formatMessage({ id: 'AUTH.LOGIN.VALIDATION_INVALID_OTP' }))
          setSubmitting(false)
          setLoading(false)
          return;
        }
        const resp = await loginVerify(formikGetOtp.values.tm_id, otp)
        const auth = {
          access_token: resp.data.data.access,
          refresh: resp.data.data.refresh,
          flow: resp.data.data.flow,
        }
        saveAuth(auth)
        navigate('/dashboard', { replace: true })
      } catch (error: any) {
        console.error(error)
        saveAuth(undefined)
        const nextFailedAttempts = failedAttempts + 1

        if (nextFailedAttempts >= MAX_FAILED_ATTEMPTS) {
          setFailedAttempts(nextFailedAttempts)
          setIsAccountLocked(true)
          setResendDisabled(true)
          setStatus(intl.formatMessage({ id: 'AUTH.LOGIN.ACCOUNT_LOCKED_ERROR' }))
          setSubmitting(false)
          setLoading(false)
          return
        }

        setFailedAttempts(nextFailedAttempts)
        setStatus(error?.response?.data?.message || intl.formatMessage({ id: 'AUTH.LOGIN.INVALID_OTP_ATTEMPTS' }, { count: MAX_FAILED_ATTEMPTS - nextFailedAttempts }))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const onKeyUpFun = (e: any, prevData, nextData) => {
    if (e.keyCode === 8 || e.keyCode === 37) {
      const prev = document.getElementById('otpInput' + prevData) as HTMLInputElement
      if (prev) {
        prev.select()
      }
    } else if (
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      (e.keyCode >= 65 && e.keyCode <= 90) ||
      (e.keyCode >= 96 && e.keyCode <= 105) ||
      e.keyCode === 39
    ) {
      const next = document.getElementById('otpInput' + nextData) as HTMLInputElement
      if (next) {
        next.select()
      }
    }
  }

  const handlePaste = (e: any) => {
    const clip = e.clipboardData.getData('text')
    if (!isNaN(clip) && Number.isInteger(parseInt(clip)) && clip.length >= 6) {
      const tempOTP = {
        a: clip[0],
        b: clip[1],
        c: clip[2],
        d: clip[3],
        e: clip[4],
        f: clip[5],
      }
      formikSubmitOtp.setValues((prev) => ({ ...prev, otp: tempOTP }))
      const input = document.getElementById('otpInput6') as HTMLInputElement
      input.select()
    }
  }

  return isOtpSent ? (
    <form
      className='form w-100'
      onSubmit={formikSubmitOtp.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-11'>
        {/* <img src='/media/logos/Goflipo_color_new.png' className='mh-90px' /> */}
        {theme === 'light'
          ? <img src='/media/logos/Goflipo_color_new.png' className='mh-90px' alt='GoFlipo logo' />
          : <img src='/media/logos/Goflipo_white_new.png' className='mh-90px' alt='GoFlipo logo' />
        }
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>{intl.formatMessage({ id: 'AUTH.LOGIN.SIGN_IN' })}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>{intl.formatMessage({ id: 'AUTH.LOGIN.OTP_VERIFY_SUBTITLE' })}</div>
      </div>

      {formikSubmitOtp.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formikSubmitOtp.status}</div>
        </div>
      ) : null}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          {intl.formatMessage({ id: 'AUTH.LOGIN.OTP_SECURITY_LABEL' })}
        </label>
        <div className='d-flex gap-2 mt-2'>
          <input
            className={'form-control bg-transparent h-60px fs-2x text-center'}
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikSubmitOtp.values.otp.a}
            onChange={(e) => {
              formikSubmitOtp.setValues((prev) => ({
                ...prev,
                otp: { ...prev.otp, a: e.target.value },
              }))
            }}
            id='otpInput1'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, '', 2)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikSubmitOtp.values.otp.b}
            onChange={(e) => {
              formikSubmitOtp.setValues((prev) => ({
                ...prev,
                otp: { ...prev.otp, b: e.target.value },
              }))
            }}
            id='otpInput2'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 1, 3)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikSubmitOtp.values.otp.c}
            onChange={(e) => {
              formikSubmitOtp.setValues((prev) => ({
                ...prev,
                otp: { ...prev.otp, c: e.target.value },
              }))
            }}
            id='otpInput3'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 2, 4)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikSubmitOtp.values.otp.d}
            onChange={(e) => {
              formikSubmitOtp.setValues((prev) => ({
                ...prev,
                otp: { ...prev.otp, d: e.target.value },
              }))
            }}
            id='otpInput4'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 3, 5)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikSubmitOtp.values.otp.e}
            onChange={(e) => {
              formikSubmitOtp.setValues((prev) => ({
                ...prev,
                otp: { ...prev.otp, e: e.target.value },
              }))
            }}
            id='otpInput5'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 4, 6)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikSubmitOtp.values.otp.f}
            onChange={(e) => {
              formikSubmitOtp.setValues((prev) => ({
                ...prev,
                otp: { ...prev.otp, f: e.target.value },
              }))
            }}
            id='otpInput6'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 5, '')}
          />
        </div>
      </div>
      {/* end::Form group */}

      {otpResendMessage && (
        <div className={`alert alert-${otpResendMessage.type === 'success' ? 'success' : 'danger'} mt-2`}>
          <div className='alert-text'>{otpResendMessage.text}</div>
        </div>
      )}
      <div className='d-grid mb-8 justify-content-end'>
        <button
          type='button'
          className='btn btn-link p-0'
          disabled={loading || resendDisabled || isAccountLocked}
          onClick={handleResendOtp}
        >
          {(
            <span className='indicator-label'>
              {isAccountLocked
                ? intl.formatMessage({ id: 'AUTH.LOGIN.ACCOUNT_LOCKED' })
                : resendDisabled
                  ? intl.formatMessage({ id: 'AUTH.LOGIN.RESEND_COOLDOWN' }, { time: resendTimer })
                  : intl.formatMessage({ id: 'AUTH.LOGIN.RESEND_BUTTON' })}
            </span>
          )}

        </button>
      </div>
      <p className="text-warning text-center">
        {intl.formatMessage({ id: 'AUTH.LOGIN.WARNING_MAX_RETRIES' })}
      </p>
      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formikSubmitOtp.isSubmitting || !formikSubmitOtp.isValid || isAccountLocked}
        >
          {!loading && <span className='indicator-label'>{intl.formatMessage({ id: 'AUTH.LOGIN.SUBMIT_OTP_BUTTON' })}</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {intl.formatMessage({ id: 'COMMON.MESSAGES.PLEASE_WAIT' })}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  ) : (
    <form
      className='form w-100'
      onSubmit={formikGetOtp.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-11'>
        {/* <img src='/media/logos/Goflipo_color_new.png' className='mh-90px' /> */}
        {theme === 'light'
          ? <img src='/media/logos/Goflipo_color_new.png' className='mh-90px' alt='GoFlipo logo' />
          : <img src='/media/logos/Goflipo_white_new.png' className='mh-90px' alt='GoFlipo logo' />
        }
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>{intl.formatMessage({ id: 'AUTH.LOGIN.TM_PANEL_TITLE' })}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {intl.formatMessage({
            id: featureFlags.onboarding ? 'AUTH.LOGIN.TM_LA_SUBTITLE' : 'AUTH.LOGIN.TM_SUBTITLE',
            defaultMessage: featureFlags.onboarding
              ? 'Enter your Telemarketer ID or email address to continue'
              : undefined,
          })}
        </div>
      </div>

      {formikGetOtp.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formikGetOtp.status}</div>
        </div>
      ) : null}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <div className='d-flex align-items-center gap-2 mb-2'>
          <label className='form-label fs-6 fw-bolder text-dark mb-0'>
            {intl.formatMessage({
              id: featureFlags.onboarding ? 'AUTH.LOGIN.TM_ID_EMAIL_LABEL' : 'AUTH.LOGIN.TM_ID_LABEL',
              defaultMessage: featureFlags.onboarding ? 'Telemarketer ID / Email' : 'TM ID',
            })}
          </label>
          {featureFlags.onboarding && (
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-primary w-20px h-20px rounded-circle p-0'
              onClick={() => setShowInfo((v) => !v)}
              title='Help'
            >
              <i className='bi bi-info fs-8' />
            </button>
          )}
        </div>

        {featureFlags.onboarding && showInfo && (
          <div className='bg-light-primary border border-primary border-opacity-25 rounded-2 p-4 mb-4'>
            <p className='fs-7 fw-bold text-dark mb-3'>
              {intl.formatMessage({ id: 'AUTH.LOGIN.INFO_TITLE', defaultMessage: 'Which credential should I use?' })}
            </p>
            <div className='d-flex gap-3 mb-3'>
              <span className='badge badge-light-warning fs-8 text-nowrap align-self-start mt-1 text-center' style={{ minWidth: '120px' }}>
                {intl.formatMessage({ id: 'AUTH.LOGIN.INFO_EMAIL_HEAD', defaultMessage: 'KYB Under Review' })}
              </span>
              <p className='fs-7 text-gray-700 mb-0'>
                {intl.formatMessage({ id: 'AUTH.LOGIN.INFO_EMAIL_BODY', defaultMessage: 'Log in with your verified email address if your KYB application is currently under review.' })}
              </p>
            </div>
            <div className='d-flex gap-3'>
              <span className='badge badge-light-success fs-8 text-nowrap align-self-start mt-1 text-center' style={{ minWidth: '120px' }}>
                {intl.formatMessage({ id: 'AUTH.LOGIN.INFO_TM_ID_HEAD', defaultMessage: 'KYB Approved' })}
              </span>
              <p className='fs-7 text-gray-700 mb-0'>
                {intl.formatMessage({ id: 'AUTH.LOGIN.INFO_TM_ID_BODY', defaultMessage: 'Log in with your TM ID once your KYB application has been approved.' })}
              </p>
            </div>
          </div>
        )}

        <input
          placeholder={intl.formatMessage({
            id: featureFlags.onboarding ? 'AUTH.LOGIN.TM_ID_EMAIL_PLACEHOLDER' : 'AUTH.LOGIN.TM_ID_PLACEHOLDER',
            defaultMessage: featureFlags.onboarding ? 'Telemarketer ID or email address' : 'TM ID',
          })}
          {...formikGetOtp.getFieldProps('tm_id')}
          className={clsx(
            'form-control bg-transparent'
          )}
          type='text'
          name='tm_id'
          autoComplete='off'
        />
        {formikGetOtp.touched.tm_id && formikGetOtp.errors.tm_id && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formikGetOtp.errors.tm_id}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formikGetOtp.isSubmitting || !formikGetOtp.isValid}
        >
          {!loading && <span className='indicator-label'>{intl.formatMessage({ id: 'AUTH.LOGIN.GET_OTP_BUTTON' })}</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {intl.formatMessage({ id: 'COMMON.MESSAGES.PLEASE_WAIT' })}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      {featureFlags.onboarding && (
        <div className='text-gray-500 text-center fw-semibold fs-6'>
          {intl.formatMessage({ id: 'AUTH.LOGIN.NEW_USER_PROMPT' })}{' '}
          <Link to='/onboarding' className='link-primary fw-bold'>
            {intl.formatMessage({ id: 'AUTH.LOGIN.SIGNUP_LINK' })}
          </Link>
        </div>
      )}

      {/* <div className='text-gray-500 text-center fw-semibold fs-6'>
        Need any help ?{' '}
        <Link to='#' className='link-primary'>
          Contact Us
        </Link>
      </div> */}
    </form>
  )
}
